import { ReactNode } from 'react';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import CloudIcon from '@liquidweb-dev/undercurrent/icons/lucide/Cloud';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import useProductConfigIntent from 'modules/customerIntent/productConfiguration';
import { BackupOption } from 'modules/backups/helpers';
import useBrandName from 'utility/hooks/useBrandName';

interface CloudBackupSettingsContentProps {
	backupType: string;
	onBackupTypeChange: (value: string) => void;
	isLoading?: boolean;
	backupOptions?: BackupOption[];
	onClose: () => void;
	footerContents: ReactNode | ReactNode[];
	children: ReactNode | ReactNode[];
}

const CloudBackupSettingsContent = ({
	backupType,
	onBackupTypeChange,
	isLoading,
	backupOptions,
	onClose,
	footerContents,
	children,
}: CloudBackupSettingsContentProps) => {
	useProductConfigIntent(backupType);
	const brandName = useBrandName();

	return (
		<>
			<DrawerHeader
				onClose={onClose}
				description={
					<Typography
						variant="body-sm"
						sx={({ vars }) => ({ color: vars.palette.uc.text.medium })}
					>
						Configure your backup solution to protect your data
					</Typography>
				}
				sx={{ fontWeight: 500 }}
			>
				Backup settings
			</DrawerHeader>
			<DrawerContent>
				<Stack
					sx={{
						gap: 300,
					}}
				>
					<Stack
						sx={{
							gap: 100,
						}}
					>
						<Stack
							sx={{
								gap: 100,
								flexDirection: 'row',
								alignSelf: 'stretch',
								alignItems: 'center',
							}}
						>
							<CloudIcon
								sx={({ size }) => ({ width: size.xS, height: size.xS })}
							/>
							<Typography
								variant="title-xs"
								sx={{
									fontWeight: 500,
								}}
							>
								Cloud Backups
							</Typography>
						</Stack>
						<Typography variant="body-sm">
							{brandName} provides automatic backups essential for critical
							systems. Use a saved image to quickly restore in case of issues.
						</Typography>
					</Stack>
					<Stack
						sx={{
							gap: 150,
						}}
					>
						<Typography
							variant="label-lg"
							sx={{
								fontWeight: 500,
							}}
						>
							Select backup type
						</Typography>
						<TextField
							name="backupType"
							value={backupType}
							onChange={(e) => onBackupTypeChange(e.target.value)}
							select
							autoFocus
							disabled={isLoading}
						>
							{backupOptions?.map(({ value, label }) => (
								<MenuItem key={value} value={value}>
									{label}
								</MenuItem>
							))}
						</TextField>
					</Stack>
					{isLoading && (
						<Stack
							sx={{
								flex: 1,
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							<LoaderCircleIcon
								sx={({ size }) => ({ height: size.lg, width: size.lg })}
							/>
						</Stack>
					)}
					{!isLoading && children}
				</Stack>
			</DrawerContent>
			<DrawerFooter spacing={100}>{footerContents}</DrawerFooter>
		</>
	);
};

export default CloudBackupSettingsContent;
